import React, { useCallback, useState } from 'react'
import { Controller, useFormContext } from 'react-hook-form'

import { MedicalAppointmentFormData } from 'components/MedicalRequestForm/schema'
import { COUNTRY_STATES } from 'settings/_countryStatesSettings'
import { useDebounce } from 'hooks/useDebounce'
import { Option } from '../../types/option'
import { SearchSelect } from '../SearchMultiSelect'

const states = COUNTRY_STATES.map((state) => ({
  id: state.code,
  name: state.name,
}))

export function ProfessionalDocumentStateSelect(): JSX.Element {
  const [
    professionalDocumentStateOptions,
    setProfessionalDocumentStateOptions,
  ] = useState([])
  const [searchQuery, setSearchQuery] = useState('')
  const [loading, setLoading] = useState(false)
  const debouncedSearchQuery = useDebounce(searchQuery, 300)

  const {
    control,
    watch,
  } = useFormContext<MedicalAppointmentFormData>()
  const professionalDocumentStateSelected = watch('professionalDocumentState')
  const specialtyName = watch('specialty')?.[0]?.type

  const getProfessionalDocumentState = useCallback(
    async (query: string, page: number, isSearch = false) => {
      setLoading(true)
      try {
        const options = states.filter((state) => {
          return state.name.toLowerCase().includes(query.toLowerCase())
        })

        setProfessionalDocumentStateOptions((state) => isSearch ? options : [...state, ...options])
        return {
          pagination: {
            total: options.length,
            page: page,
          }
        }
      } finally {
        setLoading(false)
      }
    },
    []
  )

  React.useEffect(() => {
    if (debouncedSearchQuery.length >= 2) {
      getProfessionalDocumentState(debouncedSearchQuery, 1, true)
    } else if (debouncedSearchQuery.length === 0) {
      getProfessionalDocumentState('', 1, true)
    }
  }, [debouncedSearchQuery, getProfessionalDocumentState])

  React.useEffect(() => {
    getProfessionalDocumentState('', 1, true)
  }, [getProfessionalDocumentState])

  return (
    <Controller
      control={control}
      name='professionalDocumentState'
      render={({ field, fieldState: { error }}) => (
        <SearchSelect
          id='professional-document-state-select'
          {...field}
          selectedOption={professionalDocumentStateSelected as Option[]}
          onChange={field.onChange}
          options={professionalDocumentStateOptions}
          onSearchChange={setSearchQuery}
          loading={loading}
          floatingLabel={`Estado do ${specialtyName}*`}
          inputPlaceholder='Digite e/ou selecione para adicionar'
          emptyOptionsMessage='Nenhum resultado. Verifique sua digitação.'
          hasError={Boolean(error?.message)}
          messageError={error?.message}
          helperMessage='Os campos acima são dados do médico/profissional que prescreveu a terapia, e devem constar no encaminhamento.'
          loadOptions={getProfessionalDocumentState}
        />
      )}
    />
  )
}
